import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PermissionMap } from '@/types/permission.types'
import {
  rolePermissionsService,
  type ConfigurableRole,
  type RolePermissionsPayload,
} from '@/modules/users/services/role-permissions.service'

export const rolePermissionKeys = {
  all: ['role-permissions'] as const,
  list: () => [...rolePermissionKeys.all, 'list'] as const,
}

export function useRolePermissionsList(enabled = true) {
  return useQuery({
    queryKey: rolePermissionKeys.list(),
    queryFn: ({ signal }) => rolePermissionsService.list({ signal }),
    enabled,
    staleTime: 60_000,
  })
}

export function useUpdateRolePermissions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      role,
      permissions,
    }: {
      role: ConfigurableRole
      permissions: PermissionMap
    }) => rolePermissionsService.update(role, permissions),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rolePermissionKeys.list() })
    },
  })
}

export function useResetRolePermissions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => rolePermissionsService.reset(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rolePermissionKeys.list() })
    },
  })
}

export type { RolePermissionsPayload }
